import { useEffect, useState, useId } from 'react'
import type { ChangeEvent, ReactNode } from 'react'
import { Button as AkselButton } from '@navikt/ds-react/Button'
import { Select } from '@navikt/ds-react/Select'
import type { AkselSelectProps } from './AkselForm.types'
import { Fieldset } from '@navikt/ds-react/Fieldset'
import { Radio, RadioGroup } from '@navikt/ds-react/Radio'
import type { AkselRadioGroupProps } from './AkselForm.types'
import { TextField } from '@navikt/ds-react/TextField'
import { UNSAFE_Combobox as Combobox } from '@navikt/ds-react/Combobox'
import { ErrorMessage } from '@navikt/ds-react/Typography'
import { Checkbox } from '@navikt/ds-react/Checkbox'
import { Search } from '@navikt/ds-react/Search'
import { DatePicker, useDatepicker } from '@navikt/ds-react/DatePicker'
import { MonthPicker, useMonthpicker } from '@navikt/ds-react/MonthPicker'
import { Provider } from '@navikt/ds-react/Provider'
import { en, nb } from '@navikt/ds-react/locales'
// The library's own styling, and only the parts these controls need. ⛔ THE
// GLOBAL SHEETS ARE DELIBERATELY LEFT OUT — `reset.css` and `baseline.css`
// restyle `html` and `body` for the whole portal, which a field may not do.
import '@navikt/ds-css/dist/global/tokens.css'
import '@navikt/ds-css/dist/component/typography.css'
import '@navikt/ds-css/dist/component/button.css'
import '@navikt/ds-css/dist/component/form.css'
import '@navikt/ds-css/dist/component/chips.css'
import '@navikt/ds-css/dist/component/date.css'
// ⚠️ The calendar hangs in an `aksel-popover`, so date.css alone renders it with
// no surface, no border and no stacking — present in the DOM and invisible.
import '@navikt/ds-css/dist/component/popover.css'
// The one rule the library keeps behind a body restyle — see the file's note.
import './AkselForm.css'
import { useLocale } from 'flyt/lib/language/language'
import type {
  AkselFieldActionProps,
  AkselCheckboxProps,
  AkselDateFieldProps,
  AkselPickerProps,
  AkselSearchProps,
  AkselTextFieldProps,
} from './AkselForm.types'

/**
 * aksel-form — the portal's Aksel-backed form controls: a text box, a picker,
 * a tickbox, a date field, a search box.
 *
 * ⚠️ WHERE THE ADOPTION STOPS. The library is confined to this folder and to
 * `FileDrop`; the stylesheets above are imported and not one of them touches
 * `html` or `body`; and no third-party type reaches the contract file.
 *
 * ⚠️ THE SEARCH BOX IS NOT A FORM FIELD and sits here anyway: it asks its
 * question of a whole SCREEN, so the label rule below does not apply to it. It
 * shares the only thing that decides where a file lives — the same library and
 * stylesheets.
 *
 * Upload controls can use an external label row. Personal forms use the
 * library label directly, to keep one accessible label per control.
 *
 * THE PICKER IS ONE CONTROL WITH TWO NAMED CHOICES: `single` replaces the answer
 * and closes on the pick, `multi` collects chips in front of the input.
 * ⚠️ Its list is FLAT — the library offers no grouping and no headers, so a
 * caller holding a grouped vocabulary decides what to do about that before it
 * gets here. This unit will not fake a tree out of prefixes.
 *
 * THE DATE FIELD IS ONE FIELD IN THREE MODES, chosen by the served precision. A
 * day gets the day calendar; month and year both get the twelve-month grid —
 * ⚠️ THE LIBRARY HAS NO YEAR MODE, so the year is the month control asked for
 * less, read back through the one intercept named below.
 */

/**
 * ESCAPE CLOSES THE LIST, NOT THE DIALOG.
 *
 * ⚠️ THIS IS A SEAM BETWEEN TWO LIBRARIES, and it is why the hook is here and
 * not in the dialog. The picker opens its list as its OWN overlay; the dialog
 * around it is a different library's, and that one listens for Escape on the
 * document in the CAPTURE phase — so it hears the key first and shuts the whole
 * dialog, losing everything typed into it. The picker's own handler runs too
 * late to be heard.
 *
 * The window is the one place that runs EARLIER than a document capture
 * listener. So while a picker is on screen, Escape is marked handled there —
 * and only when it is aimed at an open list, or at a list with something typed
 * in it. Every other Escape passes through and still closes the dialog.
 *
 * The check reads standard ARIA off the focused element, never a class name of
 * the library's, so it survives the library restyling itself.
 */
let escapeHolders = 0
const holdEscape = (event: KeyboardEvent): void => {
  if (event.key !== 'Escape') {
    return
  }
  const focused = document.activeElement
  if (!(focused instanceof HTMLInputElement) || focused.role !== 'combobox') {
    return
  }
  if (focused.ariaExpanded === 'true' || focused.value !== '') {
    event.preventDefault()
  }
}

function useEscapeHeldInsideThePicker(): void {
  useEffect(() => {
    escapeHolders += 1
    if (escapeHolders === 1) {
      window.addEventListener('keydown', holdEscape, { capture: true })
    }
    return () => {
      escapeHolders -= 1
      if (escapeHolders === 0) {
        window.removeEventListener('keydown', holdEscape, { capture: true })
      }
    }
  }, [])
}

/** The library speaks its own strings; it is handed the portal's active language. */
function useAkselLocale(): typeof nb {
  return useLocale() === 'en' ? en : nb
}

export function AkselTextField({
  type, autoComplete, inputRef, onBlur, hideLabel = true, size = 'medium',
  id,
  label,
  value,
  error,
  disabled,
  focusRef,
  onChange,
  onFocus,
}: AkselTextFieldProps) {
  return (
    <Provider locale={useAkselLocale()}>
      <TextField
        size={size}
        className={size === 'small' ? 'portal-text-field--small' : undefined}
        {...(type === undefined ? {} : { type })}
        autoComplete={autoComplete}
        {...(onBlur === undefined ? {} : { onBlur })}
        id={id}
        label={label}
        hideLabel={hideLabel}
        value={value}
        // `error` typed as an optional STRING, so an absent one must be absent
        // rather than undefined — the project forbids the two being the same.
        {...(error === undefined ? {} : { error })}
        {...(disabled === undefined ? {} : { disabled })}
        ref={(node) => {
          inputRef?.(node)
          if (focusRef !== undefined) {
            focusRef.current = node
          }
        }}
        onChange={(event) => {
          onChange(event.target.value)
        }}
        {...(onFocus === undefined ? {} : { onFocus })}
      />
    </Provider>
  )
}

export function AkselPicker({
  hideLabel = true,
  inputRef,
  onBlur,
  id,
  label,
  options,
  selected,
  choice,
  placeholder,
  disabled,
  error,
  focusRef,
  onToggle,
}: AkselPickerProps) {
  useEscapeHeldInsideThePicker()

  // The library takes plain objects; ours are readonly, and it mutates nothing
  // but types them mutable, so the copy is here rather than at every call.
  const listed = options.map((option) => ({ label: option.label, value: option.value }))
  const picked = listed.filter((option) => selected.includes(option.value))

  return (
    <Provider locale={useAkselLocale()}>
      <Combobox
        id={id}
        label={label}
        hideLabel={hideLabel}
        {...(onBlur === undefined ? {} : { onBlur })}
        placeholder={placeholder}
        options={listed}
        selectedOptions={picked}
        isMultiSelect={choice === 'multi'}
        {...(error === undefined ? {} : { error })}
        {...(disabled === undefined ? {} : { disabled })}
        ref={(node) => {
          inputRef?.(node)
          if (focusRef !== undefined) {
            focusRef.current = node
          }
        }}
        onToggleSelected={(value, isSelected) => {
          onToggle(value, isSelected)
        }}
      />
    </Provider>
  )
}

export function AkselCheckbox({
  size = 'medium',
  label,
  announce,
  inputRef, onBlur, error,
  checked,
  indeterminate = false,
  disabled,
  onToggle,
}: AkselCheckboxProps) {
  const errorId = useId()
  return (
    <Provider locale={useAkselLocale()}>
      <Checkbox
        size={size}
        checked={checked}
        indeterminate={indeterminate}
        {...(inputRef === undefined ? {} : { ref: inputRef })}
        {...(onBlur === undefined ? {} : { onBlur })}
        error={error !== undefined}
        {...(error === undefined ? {} : { 'aria-describedby': errorId })}
        {...(disabled === undefined ? {} : { disabled })}
        {...(announce === undefined ? {} : { 'aria-label': announce })}
        onChange={() => {
          onToggle()
        }}
      >
        {label}
      </Checkbox>
      {error !== undefined && <ErrorMessage id={errorId}>{error}</ErrorMessage>}
    </Provider>
  )
}

// ── The date field, and the three shapes the precision asks it to take ──

const pad2 = (value: number): string => String(value).padStart(2, '0')

/**
 * ⚠️ BUILT FROM LOCAL PARTS, NEVER `toISOString()`. That converts to UTC first,
 * so a date picked in the evening comes back as the day before for anyone east
 * of Greenwich — which is everyone here.
 */
const isoDayOf = (date: Date): string =>
  `${String(date.getFullYear())}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`

const isoMonthOf = (date: Date): string => isoDayOf(date).slice(0, 7)

const isoYearOf = (date: Date): string => String(date.getFullYear())

/**
 * The caller's ISO as the library's own `Date`. Midday, so no zone can move it.
 * The month and the day are OPTIONAL, because the three precisions hand in three
 * widths — `2025`, `2025-08`, `2025-08-27` — and each missing part falls to the
 * first one there is.
 */
const dateOfIso = (iso: string): Date | undefined => {
  const parts = /^(\d{4})(?:-(\d{2})(?:-(\d{2}))?)?$/.exec(iso)
  const year = parts?.[1]
  if (year === undefined) {
    return undefined
  }
  return new Date(Number(year), Number(parts?.[2] ?? '1') - 1, Number(parts?.[3] ?? '1'), 12)
}

/** The library takes a language name, not the object its other parts want. */
function useAkselDateLocale(): 'en' | 'nb' {
  return useLocale() === 'en' ? 'en' : 'nb'
}

/**
 * The one bridge between a field that is CONTROLLED from outside and a library
 * hook that is not.
 *
 * ⚠️ IT NEVER SYNCS TO EMPTY. `setSelected(undefined)` wipes the text in the
 * box, and a half-typed date reads as empty — syncing it would delete the
 * keystroke just made. So only a WHOLE date that differs from the one the field
 * holds is written in, which is the case that matters: a suggestion arriving
 * from the outside after the document type is picked.
 */
function useDateFromOutside(
  wanted: string,
  held: string,
  apply: (date: Date | undefined) => void,
): void {
  useEffect(() => {
    if (wanted === '' || wanted === held) {
      return
    }
    const date = dateOfIso(wanted)
    if (date !== undefined) {
      apply(date)
    }
    // `apply` is a fresh function on every render of the library's hook, so this
    // effect runs on every render. That is fine and not the mechanism: the two
    // guards above are what stop it, and they close after one write.
  }, [wanted, held, apply])
}

type DatePartProps = Omit<AkselDateFieldProps, 'precision'>

function AkselDayField({ id, label, value, error, disabled, focusRef, onChange }: DatePartProps) {
  // Read ONCE: the value at mount is all the library takes; everything after
  // that arrives through the bridge below.
  const [mounted] = useState(() => dateOfIso(value))
  const { datepickerProps, inputProps, selectedDay, setSelected } = useDatepicker({
    locale: useAkselDateLocale(),
    // The mask the portal has always shown for a day. The box still accepts
    // free typing — the library parses it and lights the calendar when what is
    // typed is a whole date.
    inputFormat: 'dd.MM.yyyy',
    ...(mounted === undefined ? {} : { defaultSelected: mounted }),
    onDateChange: (date) => {
      onChange(date === undefined ? '' : isoDayOf(date))
    },
  })
  useDateFromOutside(value, selectedDay === undefined ? '' : isoDayOf(selectedDay), setSelected)

  return (
    <Provider locale={useAkselLocale()}>
      <DatePicker {...datepickerProps}>
        <DatePicker.Input
          {...inputProps}
          id={id}
          label={label}
          hideLabel
          {...(error === undefined ? {} : { error })}
          {...(disabled === undefined ? {} : { disabled })}
          ref={(node) => {
            if (focusRef !== undefined) {
              focusRef.current = node
            }
          }}
        />
      </DatePicker>
    </Provider>
  )
}

/**
 * MONTH AND YEAR ARE THE SAME CONTROL IN TWO MODES. One box, one calendar
 * button, one twelve-month grid under a year header — the mode only decides how
 * WIDE the answer is and how the box writes it back: `08.2026`, or `2026`.
 */
function AkselMonthField({
  id,
  label,
  value,
  error,
  disabled,
  focusRef,
  onChange,
  unit,
}: DatePartProps & { unit: 'month' | 'year' }) {
  const isoOf = unit === 'year' ? isoYearOf : isoMonthOf
  const [mounted] = useState(() => dateOfIso(value))
  const { monthpickerProps, inputProps, selectedMonth, setSelected } = useMonthpicker({
    locale: useAkselDateLocale(),
    // ⛔ NOT the library's default «mars 2026». The portal asks for a month the
    // same way it asks for a day — digits and dots — so the two boxes in one
    // form cannot disagree about what a date looks like. A year is just the
    // narrowest of the three.
    inputFormat: unit === 'year' ? 'yyyy' : 'MM.yyyy',
    ...(mounted === undefined ? {} : { defaultSelected: mounted }),
    onMonthChange: (date) => {
      onChange(date === undefined ? '' : isoOf(date))
    },
  })
  useDateFromOutside(value, selectedMonth === undefined ? '' : isoOf(selectedMonth), setSelected)

  /**
   * ⚠️ THE LIBRARY'S PARSER IGNORES `inputFormat`. It walks a HARD-CODED list of
   * accepted shapes (`date-utils/parse-date.js`) and a bare year is not on it,
   * so the format above alone gives a box that SHOWS «2026» and then refuses to
   * read it back. Four digits are caught here and handed to the library as the
   * first of January. Every other keystroke stays the library's own business.
   */
  const onType = (event: ChangeEvent<HTMLInputElement>): void => {
    if (unit === 'year' && /^\d{4}$/.test(event.target.value)) {
      setSelected(new Date(Number(event.target.value), 0, 1, 12))
      return
    }
    inputProps.onChange?.(event)
  }

  return (
    <Provider locale={useAkselLocale()}>
      <MonthPicker {...monthpickerProps}>
        <MonthPicker.Input
          {...inputProps}
          onChange={onType}
          id={id}
          label={label}
          hideLabel
          {...(error === undefined ? {} : { error })}
          {...(disabled === undefined ? {} : { disabled })}
          ref={(node) => {
            if (focusRef !== undefined) {
              focusRef.current = node
            }
          }}
        />
      </MonthPicker>
    </Provider>
  )
}

export function AkselDateField({ precision, ...rest }: AkselDateFieldProps) {
  // Switching precision REMOUNTS the field, because the element type changes —
  // and that is wanted: a month must not go on carrying the day somebody picked
  // before the document type was known.
  if (precision === 'day') {
    return <AkselDayField {...rest} />
  }
  return <AkselMonthField {...rest} unit={precision} />
}

/** Secondary field actions use the same library and size as their adjacent control. */
export function AkselFieldAction({ size = 'medium', disabled = false, children, ...props }: AkselFieldActionProps) {
  return <AkselButton {...props} disabled={disabled} size={size} variant="secondary" data-color="neutral" className="portal-field-action shrink-0 whitespace-nowrap">
    <span className="inline-flex items-center gap-2">{children}</span>
  </AkselButton>
}

/**
 * aksel-search — the box the reader types a question into.
 *
 * `simple` REMOVES THE SEARCH BUTTON, on purpose: the portal searches as the
 * reader types, so a button to press would be a control that changes nothing.
 * ⚠️ The clear control stays — it is not the same thing, and it is the one act
 * this box has.
 *
 * ONE STRING IS BOTH THE NAME AND THE PLACEHOLDER. The box carries no visible
 * label, so the greyed-out text is the only thing that says what is being
 * searched; a screen reader is told exactly that and not something else.
 */
export function AkselSearch({ label, value, onChange }: AkselSearchProps) {
  return (
    <Provider locale={useAkselLocale()}>
      <Search
        variant="simple"
        // Toolbar search and its adjacent field action share Aksel's small size.
        // Upload controls use the medium size.
        size="small"
        label={label}
        placeholder={label}
        value={value}
        onChange={onChange}
        onClear={() => {
          // The library empties its own box either way; this is the caller
          // being told, so a screen that ranks by the query stops ranking.
          onChange('')
        }}
      />
    </Provider>
  )
}

export function AkselRadioGroup({ label, help, description, value, options, error, disabled, onChange, inputRef, onBlur }: AkselRadioGroupProps) {
  return <Provider locale={useAkselLocale()}><RadioGroup legend={help === undefined ? label : <span className="inline-flex items-center gap-1">{label}{help}</span>} description={description} value={value} onChange={onChange}
    {...(error === undefined ? {} : { error })} {...(disabled === undefined ? {} : { disabled })}>
    {options.map((option, index) => <Radio key={option.value} value={option.value} {...(index === 0 && inputRef !== undefined ? { ref: inputRef } : {})} {...(onBlur === undefined ? {} : { onBlur })}>{option.label}</Radio>)}
  </RadioGroup></Provider>
}

export function AkselSelect({ label, value, options, onChange, onBlur, inputRef, error, disabled }: AkselSelectProps) {
  return <Provider locale={useAkselLocale()}><Select label={label} value={value} onChange={(event) => onChange(event.target.value)}
    {...(onBlur === undefined ? {} : { onBlur })} {...(inputRef === undefined ? {} : { ref: inputRef })}
    {...(error === undefined ? {} : { error })} {...(disabled === undefined ? {} : { disabled })}>
    {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
  </Select></Provider>
}

/** A named group for repeated form entries, using Aksel's semantic fieldset. */
export function AkselFieldset({ legend, children, size = 'medium', action }: {
  legend: string; children: ReactNode; size?: 'small' | 'medium'; action?: ReactNode
}) {
  if (size === 'small') {
    return <Fieldset legend={legend} hideLegend size="small">
      <div className="flex min-w-0 flex-col gap-2 border-t border-border pt-3 pb-2">
        <div className="flex min-h-8 items-center justify-between gap-3">
          <span aria-hidden="true" className="text-sm font-medium text-text-muted">{legend}</span>
          {action}
        </div>
        {children}
      </div>
    </Fieldset>
  }
  return <div className="rounded-lg border border-border p-4"><Fieldset legend={legend}>
    <div className="flex flex-col gap-4">{children}{action}</div>
  </Fieldset></div>
}


/** Optional remote lookup. The chosen value is applied by the caller; typing does not change saved fields. */
export function AkselAsyncLookup<Option extends { value: string; label: string }>({ label, description, emptyText, failureText, load, onSelect, disabled }: {
  label: string; description: string; emptyText: string; failureText: string;
  load: (query: string, signal: AbortSignal) => Promise<Option[]>;
  onSelect: (option: Option) => void; disabled: boolean;
}) {
  useEscapeHeldInsideThePicker()
  const [query, setQuery] = useState('')
  const [selection, setSelection] = useState<Option | null>(null)
  const [result, setResult] = useState<{ query: string; options: Option[]; failed: boolean } | null>(null)
  const active = query.trim().length >= 3 && !disabled
  const current = result?.query === query ? result : null
  useEffect(() => {
    if (!active) return
    const controller = new AbortController()
    const timer = window.setTimeout(() => {
      void load(query.trim(), controller.signal).then((options) => {
        if (!controller.signal.aborted) setResult({ query, options, failed: false })
      }).catch(() => {
        if (!controller.signal.aborted) setResult({ query, options: [], failed: true })
      })
    }, 300)
    return () => { window.clearTimeout(timer); controller.abort() }
  }, [query, active, load])
  const options = active ? current?.options ?? [] : []
  return <Provider locale={useAkselLocale()}>
    <Combobox label={label} description={description} options={options} filteredOptions={options}
      selectedOptions={selection ? [selection] : []} value={query} onChange={(value) => { setQuery(value); setResult(null) }}
      isLoading={active && current === null} disabled={disabled} toggleListButton={false}
      onToggleSelected={(value, selected) => {
        const option = options.find((item) => item.value === value)
        if (selected && option) {
          setSelection(option)
          onSelect(option)
        } else if (!selected) setSelection(null)
      }} />
    {active && current && options.length === 0 && <p className="mt-2 text-sm" role="status">{current.failed ? failureText : emptyText}</p>}
  </Provider>
}

export function AkselNativeSelect({ label, children, value, onChange }: {
  label: string; children: ReactNode; value: string; onChange: (event: ChangeEvent<HTMLSelectElement>) => void;
}) {
  return <Provider locale={useAkselLocale()}><Select value={value} onChange={onChange} label={label} hideLabel size="small">{children}</Select></Provider>
}
